import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { collection, query, where, getDocs, doc, addDoc, updateDoc, increment, setDoc, deleteDoc, onSnapshot } from 'firebase/firestore';

import { AuthContext } from '../App';

// Re-using MessageBox with updated styling
function MessageBox({ message, onClose }) {
  if (!message) return null;
  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full text-center">
        <p className="text-lg font-semibold text-gray-800 mb-6">{message}</p>
        <button
          onClick={onClose}
          className="px-6 py-3 rounded-lg shadow-md text-white font-semibold transition-all duration-300 hover:shadow-lg"
          style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)' }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          OK
        </button>
      </div>
    </div>
  );
}

export default function DiscoverPage() {
  const { userId, userProfile, db, isAuthReady } = useContext(AuthContext);
  const [profiles, setProfiles] = useState([]);
  const [allProfiles, setAllProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [sentInterests, setSentInterests] = useState({});
  const [shortlistedUsers, setShortlistedUsers] = useState({});
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  
  
  const initialFetchDone = useRef(false);
  
  const [filters, setFilters] = useState({
    minAge: '',
    maxAge: '',
    country: '',
    church: ''
  });

  // Helper function to calculate age
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  // Function to fetch profiles
  const fetchProfiles = useCallback(async () => {
    if (!isAuthReady || !userId || !db) {
      if (isAuthReady && !userId) {
        setLoading(false);
        setProfiles([]);
        setMessage('Please log in to discover profiles.');
      }
      return;
    }

    if (loading && initialFetchDone.current) {
      return;
    }

    try {
      setLoading(true);
      console.log("Fetching profiles for user:", userId);

      let blocked = {};
      try {
        const blockedUsersRef = collection(db, `users/${userId}/blockedUsers`);
        const blockedSnapshot = await getDocs(blockedUsersRef);
        
        blockedSnapshot.docs.forEach(doc => {
          blocked[doc.id] = true;
        });
        
        console.log("Blocked users:", blocked);
      } catch (blockedError) {
        console.warn("Could not fetch blocked users:", blockedError);
      }

      const usersRef = collection(db, 'users');

      // Use onSnapshot with proper real-time listener setup
      const unsubscribeUsers = onSnapshot(usersRef, (usersSnapshot) => {
        console.log("Profiles updated - Total documents in users collection:", usersSnapshot.docs.length);

        const fetchedProfiles = usersSnapshot.docs
          .map(doc => {
            const data = doc.data();
            console.log("User document:", doc.id, data);
            return { id: doc.id, ...data };
          })
          .filter(profile => {
            const isNotCurrentUser = profile.id !== userId;
            const isUser = profile.role === 'user' || profile.role === undefined || profile.role === null;
            const isNotAdmin = profile.role !== 'admin' && profile.role !== 'super_admin';
            const isNotBlocked = !blocked[profile.id];
            
            console.log(`Profile ${profile.id}:`, {
              isNotCurrentUser,
              isUser,
              isNotAdmin,
              isNotBlocked,
              role: profile.role,
              email: profile.email,
              firstName: profile.firstName,
              profilePhotoUrl: profile.profilePhotoUrl
            });
            
            return isNotCurrentUser && isUser && isNotAdmin && isNotBlocked;
          });

        console.log("Filtered profiles:", fetchedProfiles);

        // Update state with new profiles - NOW includes photo updates
        setAllProfiles(fetchedProfiles);
// Don't directly set profiles here - let the filter effect handle it
setLoading(false);
      }, (error) => {
        console.error("Error fetching discover profiles:", error);
        setMessage(`Error loading profiles: ${error.message}`);
        setLoading(false);
      });

      
      initialFetchDone.current = true;
      
      return unsubscribeUsers;

    } catch (error) {
      console.error("Error fetching discover profiles:", error);
      setMessage(`Error loading profiles: ${error.message}`);
      setLoading(false);
    }
  }, [isAuthReady, userId, db,loading]);
  
  useEffect(() => {
    let unsubscribe;
    
    if (!initialFetchDone.current) {
      const cleanup = fetchProfiles();
      if (cleanup && typeof cleanup === 'function') {
        unsubscribe = cleanup;
      }
    }
    
    return () => {
      if (unsubscribe && typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [fetchProfiles]);

  useEffect(() => {
    initialFetchDone.current = false;
  }, [userId]);
// Fetch user's shortlist items
useEffect(() => {
  if (!userId || !db) return;

  const shortlistRef = collection(db, "shortlists");
  const q = query(shortlistRef, where("likerId", "==", userId));
  
  const unsubscribe = onSnapshot(q, (snapshot) => {
    const shortlistData = {};
    snapshot.docs.forEach(doc => {
      shortlistData[doc.data().likedUserId] = doc.id;
    });
    setShortlistedUsers(shortlistData);
    console.log("DEBUG - Shortlisted users loaded:", shortlistData);
  });

  return () => unsubscribe();
}, [userId, db]);

// Fetch user's sent interests
useEffect(() => {
  if (!userId || !db) return;

  const interestsRef = collection(db, "interests");
  const q = query(interestsRef, where("senderId", "==", userId));
  
  const unsubscribe = onSnapshot(q, (snapshot) => {
    const interestsData = {};
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      // Track interests that are pending or accepted
      if (data.status === 'pending' || data.status === 'accepted') {
        interestsData[data.receiverId] = true;
      }
    });
    setSentInterests(interestsData);
    console.log("DEBUG - Sent interests loaded:", interestsData);
  });

  return () => unsubscribe();
}, [userId, db]);



  // Apply filters to profiles
  const applyFilters = useCallback(() => {
  console.log("=== FILTER DEBUG ===");
  console.log("Applying filters:", filters);
  console.log("All profiles count:", allProfiles.length);
  
  if (allProfiles.length === 0) return;

  // ADD THIS CHECK - if all filters are empty, show all profiles
  const hasAnyFilter = filters.minAge || filters.maxAge || filters.country.trim() || filters.church.trim();
  if (!hasAnyFilter) {
    setProfiles(allProfiles);
    return;
  }

  

    let filteredProfiles = [...allProfiles];

    

    // Age filter - INDEPENDENT (works even if other fields are empty)
    if (filters.minAge || filters.maxAge) {
      const beforeCount = filteredProfiles.length;
      filteredProfiles = filteredProfiles.filter(profile => {
        const age = calculateAge(profile.dateOfBirth || profile.dob);
        
        if (age === null) {
          // If no age data, show the profile (don't filter out)
          return true;
        }
        
        const minAge = filters.minAge ? parseInt(filters.minAge) : 0;
        const maxAge = filters.maxAge ? parseInt(filters.maxAge) : 200;
        
        const matches = age >= minAge && age <= maxAge;
        console.log(`${profile.firstName}: age=${age}, matches=${matches}`);
        return matches;
      });
      console.log(`Age filter: ${beforeCount} -> ${filteredProfiles.length} profiles`);
    }

    // Country filter - INDEPENDENT (only applies if country filter is set)
    if (filters.country.trim()) {
      const beforeCount = filteredProfiles.length;
      filteredProfiles = filteredProfiles.filter(profile => 
        profile.country && 
        profile.country.toLowerCase().includes(filters.country.toLowerCase().trim())
      );
      console.log(`Country filter: ${beforeCount} -> ${filteredProfiles.length} profiles`);
    }

    // Church filter - INDEPENDENT (only applies if church filter is set)
    if (filters.church.trim()) {
      const beforeCount = filteredProfiles.length;
      filteredProfiles = filteredProfiles.filter(profile => 
        profile.church && 
        profile.church.toLowerCase().includes(filters.church.toLowerCase().trim())
      );
      console.log(`Church filter: ${beforeCount} -> ${filteredProfiles.length} profiles`);
    }

    console.log(`Final filtered profiles: ${filteredProfiles.length}`);
    setProfiles(filteredProfiles);
  }, [allProfiles, filters]);

  useEffect(() => {
  applyFilters();
}, [applyFilters]);



  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      minAge: '',
      maxAge: '',
      country: '',
      church: ''
    });
  };

  const handleSearch = () => {
    applyFilters();
  };

  const getUniqueCountries = () => {
    const countries = allProfiles
      .map(profile => profile.country)
      .filter(country => country && country.trim())
      .map(country => country.trim());
    return [...new Set(countries)].sort();
  };

  const getUniqueChurches = () => {
    const churches = allProfiles
      .map(profile => profile.church)
      .filter(church => church && church.trim())
      .map(church => church.trim());
    return [...new Set(churches)].sort();
  };

  const handleExpressInterest = async (targetUserId) => {
    setMessage('');
    if (!userId) {
      setMessage('You must be logged in to express interest.');
      return;
    }

    if (sentInterests[targetUserId]) {
      setMessage('You have already sent an interest to this person or it has been accepted.');
      return;
    }

    try {
      const interestsCollectionRef = collection(db, 'interests');
      await addDoc(interestsCollectionRef, {
        senderId: userId,
        receiverId: targetUserId,
        status: 'pending',
        createdAt: new Date(),
      });

      setSentInterests(prev => ({
        ...prev,
        [targetUserId]: true
      }));

      try {
        const receiverDocRef = doc(db, 'users', targetUserId);
        await updateDoc(receiverDocRef, {
          interestsReceived: increment(1)
        });
        setMessage('Interest expressed successfully!');
      } catch (updateError) {
        console.warn("Could not update receiver's interestsReceived count:", updateError);
        setMessage('Interest expressed successfully! (Receiver count update may be delayed)');
      }

    } catch (error) {
      setMessage(`Failed to express interest: ${error.message}`);
      console.error("Express interest error:", error);
      
      setSentInterests(prev => {
        const updated = { ...prev };
        delete updated[targetUserId];
        return updated;
      });
    }
  };

  const handleToggleShortlist = async (targetUserId) => {
  console.log("=== SHORTLIST DEBUG ===");
  console.log("userId:", userId);
  console.log("userProfile:", userProfile);
  console.log("userProfile?.plan:", userProfile?.plan);
  console.log("targetUserId:", targetUserId);
  
  if (!userId) {
    setMessage('You must be logged in to manage your shortlist.');
    return;
  }

  // Check if user is on free plan
 const isFreeUser = userProfile?.membershipType !== 'premium';
  console.log("isFreeUser:", isFreeUser);
  console.log("shortlistedUsers:", shortlistedUsers);
    
    const currentShortlistDocId = shortlistedUsers[targetUserId];
    const isCurrentlyShortlisted = !!currentShortlistDocId;

    // If free user is trying to add to shortlist (not remove), show upgrade message
    if (isFreeUser && !isCurrentlyShortlisted) {
      setMessage('Upgrade to Premium to unlock the shortlist feature and save your favorite profiles!');
      return;
    }

    try {
      if (isCurrentlyShortlisted) {
        const docRef = doc(db, "shortlists", currentShortlistDocId);
        await deleteDoc(docRef);

        setShortlistedUsers(prev => {
          const updated = { ...prev };
          delete updated[targetUserId];
          return updated;
        });

        setMessage("Profile removed from your shortlist.");
      } else {
        const shortlistRef = collection(db, "shortlists");
        const docRef = await addDoc(shortlistRef, {
          likerId: userId,
          likedUserId: targetUserId,
          createdAt: new Date()
        });

        setShortlistedUsers(prev => ({
          ...prev,
          [targetUserId]: docRef.id
        }));

        setMessage("Profile added to your shortlist!");
      }
    } catch (error) {
      console.error("Error toggling shortlist:", error);
      setMessage(`Error updating shortlist: ${error.message}`);
    }
  };

  const handleBlockUser = async (targetUserId) => {
    if (!userId) {
      setMessage('You must be logged in to block users.');
      return;
    }

    try {
      const blockedUserDocRef = doc(db, `users/${userId}/blockedUsers`, targetUserId);
      await setDoc(blockedUserDocRef, {
        blockedAt: new Date()
      });

      

      setProfiles(prev => prev.filter(profile => profile.id !== targetUserId));
      setAllProfiles(prev => prev.filter(profile => profile.id !== targetUserId));

      setMessage("User has been blocked successfully.");
    } catch (error) {
      console.error("Error blocking user:", error);
      setMessage(`Error blocking user: ${error.message}`);
    }
  };

  const handleRefresh = () => {
    initialFetchDone.current = false;
    fetchProfiles();
  };

  if (userProfile?.role === 'super_admin' || userProfile?.role === 'admin') {
    return (
      <div className="container mx-auto p-6 bg-gray-50 rounded-xl shadow-lg my-8">
        <div className="bg-white p-12 rounded-2xl shadow-md text-center">
          <h2 className="text-2xl font-semibold mb-4" style={{ color: 'hsl(221, 45%, 18%)' }}>Access Restricted</h2>
          <p className="text-gray-600">Admin users cannot access the discover page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto px-6 py-8 mt-20">
        <h2 className="text-4xl font-extrabold mb-6 text-center" style={{ color: 'hsl(221, 45%, 18%)' }}>Discover Profiles</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(8)].map((_, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col">
              <div className="h-48 w-full bg-gray-200 animate-pulse"></div>
              <div className="p-4 flex-grow flex flex-col">
                <div className="h-6 bg-gray-200 rounded animate-pulse mb-2"></div>
                <div className="h-4 bg-gray-200 rounded animate-pulse mb-2 w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded animate-pulse mb-4 w-1/2"></div>
                <div className="h-16 bg-gray-200 rounded animate-pulse mb-4"></div>
                <div className="mt-auto pt-4 border-t border-gray-100 space-y-2">
                  <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
                  <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-8 mt-20">
      <h2 className="text-4xl font-extrabold mb-2 text-center" style={{ color: 'hsl(221, 45%, 18%)' }}>Discover Profiles</h2>
      <p className="text-lg text-gray-600 mb-8 text-center">Find your divine match among available profiles</p>

      {/* Advanced Search Toggle */}
      <div className="mb-6 text-center">
        <button
          onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
          className="inline-flex items-center px-6 py-3 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
          style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)' }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          {showAdvancedSearch ? 'Hide Filters' : 'Advanced Search'}
        </button>
      </div>

      {/* Advanced Search Panel */}
      {showAdvancedSearch && (
        <div className="bg-white p-6 rounded-2xl shadow-xl mb-8">
          <h3 className="text-lg font-semibold mb-4" style={{ color: 'hsl(221, 45%, 18%)' }}>Search Filters</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Age Range */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'hsl(221, 45%, 18%)' }}>Age Range</label>
              <div className="flex space-x-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={filters.minAge}
                  onChange={(e) => handleFilterChange('minAge', e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 transition duration-200 outline-none"
                  min="18"
                  max="100"
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={filters.maxAge}
                  onChange={(e) => handleFilterChange('maxAge', e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 transition duration-200 outline-none"
                  min="18"
                  max="100"
                />
              </div>
            </div>

            {/* Country */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'hsl(221, 45%, 18%)' }}>Country</label>
              <select
                value={filters.country}
                onChange={(e) => handleFilterChange('country', e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 transition duration-200 outline-none"
              >
                <option value="">All Countries</option>
                {getUniqueCountries().map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>

            {/* Church */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'hsl(221, 45%, 18%)' }}>Church</label>
              <select
                value={filters.church}
                onChange={(e) => handleFilterChange('church', e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 transition duration-200 outline-none"
              >
                <option value="">All Churches</option>
                {getUniqueChurches().map(church => (
                  <option key={church} value={church}>{church}</option>
                ))}
              </select>
            </div>

            {/* Action Buttons */}
            <div className="flex items-end space-x-2">
              <button
                onClick={handleSearch}
                className="flex-1 px-4 py-2 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-300"
                style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)' }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                Search
              </button>
              <button
                onClick={clearFilters}
                className="px-3 py-2 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-300 text-sm"
                style={{ background: 'hsl(221, 45%, 18%)', color: 'white' }}
              >
                Clear
              </button>
            </div>
          </div>

          {/* Results count */}
          <div className="mt-4 text-sm text-gray-600">
            Showing {profiles.length} of {allProfiles.length} profiles
          </div>
        </div>
      )}

      {message && <MessageBox message={message} onClose={() => setMessage('')} />}

      {profiles.length === 0 ? (
        <div className="bg-white p-12 rounded-2xl shadow-xl text-center">
          <div className="mx-auto mb-4 h-16 w-16 text-gray-400 flex items-center justify-center rounded-full" style={{ background: 'hsl(220, 15%, 96%)' }}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold mb-2" style={{ color: 'hsl(221, 45%, 18%)' }}>
            {allProfiles.length === 0 ? 'No Profiles Available' : 'No Profiles Match Your Filters'}
          </h3>
          <p className="text-gray-600 mb-6">
            {allProfiles.length === 0 
              ? 'No other user profiles are available at the moment. Check back later!' 
              : 'Try adjusting your search filters to find more profiles.'
            }
          </p>
          <div className="flex justify-center space-x-4">
            <button
              onClick={handleRefresh}
              className="px-6 py-3 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
              style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)' }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              Refresh
            </button>
            {allProfiles.length > 0 && (
              <button
                onClick={clearFilters}
                className="px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                style={{ background: 'hsl(221, 45%, 18%)', color: 'white' }}
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {profiles.map((profile) => {
              // Free users can only see first 5 profiles
              const isFreeUser = userProfile?.membershipType !== 'premium';
              const freeProfileLimit = 5;
              const profileIndex = profiles.findIndex(p => p.id === profile.id);
              const isLimitedProfile = isFreeUser && profileIndex >= freeProfileLimit;

              return isLimitedProfile ? (
                // Blurred placeholder for limited profiles
                <div key={profile.id + '_' + (profile.profilePhotoUrl || '')} className="bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col relative">
                  <div className="absolute inset-0 backdrop-blur-sm bg-white/30 z-10 flex items-center justify-center">
                    <div className="bg-white p-6 rounded-2xl shadow-2xl text-center max-w-xs mx-4">
                      <div className="mx-auto mb-4 h-16 w-16 flex items-center justify-center rounded-full" style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)' }}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                      <h4 className="text-lg font-bold mb-2" style={{ color: 'hsl(221, 45%, 18%)' }}>Upgrade to Premium</h4>
                      <p className="text-sm text-gray-600 mb-4">Unlock unlimited profile views and more features!</p>
                    </div>
                  </div>
                  <div className="h-56 w-full bg-gray-200 blur-lg"></div>
                  <div className="p-3 flex-grow flex flex-col blur-sm">
                    <div className="h-6 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded mb-2 w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded mb-4 w-1/2"></div>
                  </div>
                </div>
              ) : (
            <div key={profile.id + '_' + (profile.profilePhotoUrl || '')} className="bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col transform hover:shadow-2xl hover:-translate-y-1 transition-all duration-300">
              <div className="relative h-56 w-full bg-gray-200 flex items-center justify-center">
                <img
                  src={profile.profilePhotoUrl || "https://placehold.co/256x256/1e293b/ffffff?text=No+Photo&font=raleway"}
                  alt={profile.firstName || "Profile"}
                  className="w-full h-full object-cover"
                  onError={(e) => { 
                    e.target.onerror = null; 
                    e.target.src = "https://placehold.co/256x256/1e293b/ffffff?text=No+Photo&font=raleway"; 
                  }}
                />
                <button
                  onClick={() => handleToggleShortlist(profile.id)}
                  className={`absolute top-3 right-3 p-2.5 rounded-full shadow-lg transition-all duration-300 ${
                    shortlistedUsers[profile.id] 
                      ? 'text-white hover:shadow-xl' 
                      : 'bg-white text-gray-600 hover:bg-yellow-50 hover:text-yellow-600 hover:shadow-xl'
                  }`}
                  style={shortlistedUsers[profile.id] ? { background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)' } : {}}
                  title={shortlistedUsers[profile.id] ? "Remove from shortlist" : "Add to shortlist"}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill={shortlistedUsers[profile.id] ? "currentColor" : "none"} viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                </button>
              </div>
              <div className="p-3 flex-grow flex flex-col">
                <h3 className="text-lg font-bold mb-1" style={{ color: 'hsl(221, 45%, 18%)' }}>
                  {profile.firstName || 'Unknown'} {profile.lastName || ''}
                </h3>
                <p className="text-gray-600 text-sm mb-1">
                  {profile.city || 'City not specified'}{profile.state && `, ${profile.state}`}{profile.country && `, ${profile.country}`}
                </p>
                {profile.church && (
                  <p className="text-gray-600 text-sm mb-3">
                    Church: {profile.church}
                  </p>
                )}

                <div className="mt-auto pt-2 border-t border-gray-100 space-y-1.5">
                  {/* Express Interest Button */}
                  {sentInterests[profile.id] ? (
                    <button
                      className="w-full px-4 py-3 bg-gray-400 text-white rounded-xl cursor-not-allowed flex items-center justify-center font-semibold shadow-md"
                      disabled
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      Interest Sent
                    </button>
                  ) : (
                    <button
                      onClick={() => handleExpressInterest(profile.id)}
                      className="w-full px-4 py-2 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center"
                      style={{ background: 'linear-gradient(135deg, hsl(221, 45%, 18%) 0%, hsl(221, 35%, 25%) 100%)' }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                      Express Interest
                    </button>
                  )}

                  {/* Shortlist Button */}
                  <button
                    onClick={() => handleToggleShortlist(profile.id)}
                    className={`w-full px-4 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl flex items-center justify-center transition-all duration-300 ${
                      shortlistedUsers[profile.id]
                        ? 'text-white'
                        : 'text-white'
                    }`}
                    style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)' }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill={shortlistedUsers[profile.id] ? "currentColor" : "none"} viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    </svg>
                    {shortlistedUsers[profile.id] ? 'Remove from Shortlist' : 'Add to Shortlist'}
                  </button>

                  {/* Block User Button */}
                  <button
                    onClick={() => handleBlockUser(profile.id)}
                    className="w-full px-4 py-2 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg, hsl(221, 45%, 18%) 0%, hsl(221, 35%, 25%) 100%)' }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
                    </svg>
                    Block User
                  </button>
                </div>
              </div>
            </div>
              );
            })}
          </div>
          
          {/* Upgrade banner for free users */}
       {userProfile?.membershipType !== 'premium' && profiles.length > 5 &&  (
            <div className="mt-8 bg-white p-8 rounded-2xl shadow-xl text-center" style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)' }}>
              <h3 className="text-2xl font-bold text-white mb-2">Want to See More Profiles?</h3>
              <p className="text-white mb-6">Upgrade to Premium to view unlimited profiles, use advanced search filters, and unlock exclusive features!</p>
              <button
                onClick={() => window.location.href = '/pricing'}
                className="px-8 py-4 bg-white rounded-xl font-bold shadow-lg hover:shadow-2xl transition-all duration-300"
                style={{ color: 'hsl(43, 74%, 52%)' }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                Upgrade to Premium
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}